import {
  Amount,
  makeAmount,
  makePercent,
  Percent,
  PERCENT_DECIMALS,
  PRECISE_DECIMALS,
  RoundingType,
} from "@silo/types";
import { Option, pipe } from "effect";
import { shiftDecimal } from "../bigint";
import { NATIVE_DECIMALS } from "./constants";
import {
  BaseLtv,
  DebtAssetMetrics,
  DepositAssetMetrics,
  NormalizedBaseLtv,
  NormalizedDebtAssetMetrics,
  NormalizedDepositAssetMetrics,
} from "./types";

export function denormalizeBaseLtv(baseLtv: NormalizedBaseLtv): BaseLtv {
  const {
    userLtv,
    userMaxLtv,
    userLiquidationThreshold,
    healthFactor,
    riskFactor,
    depositValue,
    liquidationThresholdValue,
    availableToBorrowValue,
    borrowedValue,
    remainingToBorrowValue,
    liquidationBufferValue,
    borrowedPct,
    remainingToBorrowPct,
    depositAssets,
    debtAssets,
  } = baseLtv;

  return {
    userLtv: denormalizePercent(userLtv),
    userMaxLtv: denormalizePercent(userMaxLtv),
    userLiquidationThreshold: denormalizePercent(userLiquidationThreshold),
    healthFactor: denormalizePercent(healthFactor),
    riskFactor: denormalizePercent(riskFactor),
    depositValue: denormalizeNativeAmount(depositValue),
    liquidationThresholdValue: denormalizeNativeAmount(
      liquidationThresholdValue
    ),
    availableToBorrowValue: denormalizeNativeAmount(availableToBorrowValue),
    borrowedValue: denormalizeNativeAmount(borrowedValue),
    remainingToBorrowValue: denormalizeNativeAmount(remainingToBorrowValue),
    liquidationBufferValue: denormalizeNativeAmount(liquidationBufferValue),
    borrowedPct: denormalizePercent(borrowedPct),
    remainingToBorrowPct: denormalizePercent(remainingToBorrowPct),

    depositAssets: depositAssets.map(denormalizeDepositAssetMetrics),
    debtAssets: debtAssets.map(denormalizeDebtAssetMetrics),
  };
}

export function denormalizeDepositAssetMetrics(
  metrics: NormalizedDepositAssetMetrics
): DepositAssetMetrics {
  const {
    tokenAddress,
    decimals,
    maxWithdrawTokens: { type, value },
    liquidationPriceNative,
  } = metrics;

  return {
    tokenAddress,
    decimals,
    maxWithdrawTokens: { type, value: denormalizeAmount(value, decimals) },
    liquidationPriceNative: denormalizeLiquidationPriceNative(
      liquidationPriceNative
    ),
  };
}

export function denormalizeDebtAssetMetrics(
  metrics: NormalizedDebtAssetMetrics
): DebtAssetMetrics {
  const {
    tokenAddress,
    decimals,
    borrowedTokens,
    remainingToBorrowTokens,
    availableToBorrowTokens,
    liquidationPriceNative,
  } = metrics;

  return {
    tokenAddress,
    decimals,
    borrowedTokens: denormalizeAmount(borrowedTokens, decimals),
    // rounding down on remaining to borrow so we don't put users in a compromising health factor
    remainingToBorrowTokens: denormalizeAmount(
      remainingToBorrowTokens,
      decimals,
      "down"
    ),
    availableToBorrowTokens: denormalizeAmount(
      availableToBorrowTokens,
      decimals
    ),
    liquidationPriceNative: denormalizeLiquidationPriceNative(
      liquidationPriceNative
    ),
  };
}

export function denormalizeAmount(
  value: bigint,
  decimals: number,
  roundingType?: RoundingType
): Amount {
  return pipe(
    shiftDecimal(value, decimals - PRECISE_DECIMALS, roundingType),
    makeAmount
  );
}

export function denormalizeNativeAmount(value: bigint): Amount {
  return pipe(
    shiftDecimal(value, NATIVE_DECIMALS - PRECISE_DECIMALS),
    makeAmount
  );
}

export function denormalizePercent(value: bigint): Percent {
  return pipe(
    shiftDecimal(value, PERCENT_DECIMALS - PRECISE_DECIMALS),
    makePercent
  );
}

export function denormalizeLiquidationPriceNative(
  value: bigint | null
): Amount | null {
  return pipe(
    value,
    Option.fromNullable,
    Option.map(denormalizeNativeAmount),
    Option.getOrNull
  );
}
